import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

/** Why a row sorted where it did — shown as a chip so the order reads as deliberate. */
export type QueueReason = 'CALLBACK_DUE' | 'PTP_DUE_TODAY' | 'PRIORITY'

export type Mood = 'NORMAL' | 'ANGER' | 'IRATE'

export interface ConductBlock {
  code: 'dnc_blocked' | 'contact_hours_blocked' | 'contact_frequency_exceeded' | string
  message: string
  overridable: boolean
  requiredPermission: string | null
}

/**
 * One case on the telecaller's shift plan.
 *
 * Note what is absent: any phone number. The queue never carries one — the
 * console resolves it per case through the audited PII read, so opening a
 * queue does not disclose a page of customer numbers at once.
 */
export interface TeleQueueRow {
  caseId: string
  loanAccountNo: string
  customerName: string
  productType: string | null
  currentDpd: number | null
  currentBucket: string | null
  /** POS — portfolio context. Never the figure an agent quotes. */
  currentOutstanding: string | null
  /** The demand: overdue principal + interest + all charges. */
  currentOverdue: string | null
  priorityBand: string | null
  priorityScore: number
  branchCode: string | null
  preferredLanguage: string | null
  callbackAt: string | null
  lastMood: Mood | null
  ptpDueOn: string | null
  ptpAmount: string | null
  contactCountToday: number
  frequencyCap: number
  /** Annotated, never filtered: a blocked case is greyed with its reason, not hidden. */
  conductBlocks: ConductBlock[]
  queueReason: QueueReason
}

/**
 * The shift plan. Ordered server-side (callbacks → promises due today →
 * priority) and deliberately NOT re-sortable here: the ordering is the
 * product, and letting the client re-sort turns it back into a case list.
 */
export function useTeleQueue(limit = 25) {
  return useQuery({
    queryKey: ['tele', 'queue', limit],
    queryFn: async () => {
      const res = await api.get<TeleQueueRow[]>('/v1/tele/queue', { params: { limit } })
      return res.data
    },
    // A callback falling due, or a colleague taking a case, both change this
    // list underneath the agent. Short enough to stay honest, long enough not
    // to yank a row out from under a click.
    refetchInterval: 30_000,
  })
}


// ---------------------------------------------------------------------------
// Click-to-dial
//
// The browser never holds a dialable number. It reads masked labels, posts a
// LABEL to dial, and the server resolves it. So there is no number in the
// network tab, no number in a console log, and the label is the only
// identifier either side ever handled.
// ---------------------------------------------------------------------------

export interface ContactNumber {
  /** MOBILE_PRIMARY | MOBILE_SECONDARY | REFERENCE_<n> */
  ref: string
  label: string
  /** Last four digits only. Never dialable. */
  masked: string
}

export type CallStatus = 'RINGING' | 'CONNECTED' | 'COMPLETED' | 'NO_ANSWER' | 'BUSY' | 'FAILED'

export interface DialResponse {
  providerCallId: string
  status: CallStatus
  startedAt: string
  /** MOCK, or the vendor — the console badges a simulated call so nobody
   *  mistakes a demo for a real conversation. */
  provider: string
}

export interface CallState {
  providerCallId: string
  status: CallStatus
  terminal: boolean
  /** Canonical contact_result to pre-select; null when the agent must decide. */
  suggestedContactResult: string | null
  startedAt: string | null
  endedAt: string | null
  recordingUrl: string | null
  failureReason: string | null
}

export function useContactNumbers(caseId: string | null | undefined) {
  return useQuery({
    queryKey: ['tele', 'contact-numbers', caseId],
    enabled: !!caseId,
    // Each fetch writes a PII access row, so this must not re-fetch on every
    // window focus — that would fill the DPO report with noise and bury the
    // reads that matter.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await api.get<ContactNumber[]>(`/v1/tele/cases/${caseId}/contact-numbers`)
      return res.data
    },
  })
}

export function useDial() {
  return useMutation({
    mutationFn: async (body: { caseId: string; dialledNumberRef: string }) => {
      const res = await api.post<DialResponse>('/v1/tele/calls', body)
      return res.data
    },
  })
}

/**
 * Polls a live call. Stops as soon as the call reaches a terminal state AND a
 * recording has appeared — a provider publishes the recording slightly after
 * hangup, so stopping at "terminal" alone leaves the player permanently empty.
 */
export function useCallState(providerCallId: string | null) {
  return useQuery({
    queryKey: ['tele', 'call', providerCallId],
    enabled: !!providerCallId,
    refetchInterval: (q) => {
      const s = q.state.data as CallState | undefined
      if (!s) return 1500
      if (!s.terminal) return 1500
      return s.recordingUrl ? false : 3000
    },
    queryFn: async () => {
      const res = await api.get<CallState>(`/v1/tele/calls/${providerCallId}`)
      return res.data
    },
  })
}

export function useHangup() {
  return useMutation({
    mutationFn: async (providerCallId: string) => {
      await api.post(`/v1/tele/calls/${providerCallId}/hangup`)
    },
  })
}

// ---------------------------------------------------------------------------
// Payment request
//
// The same mock gateway the mobile collection journey uses — a telecaller
// raising a UPI link mid-call and a field agent raising a QR at the doorstep
// are the same act through the same path, so they share one implementation
// rather than two that drift.
// ---------------------------------------------------------------------------

export type PaymentChannel = 'UPI_LINK' | 'UPI_QR'

export interface Payment {
  paymentId: string
  paymentRef: string
  status: 'CREATED' | 'PENDING' | 'SUCCESS' | 'FAILED' | 'EXPIRED' | 'CANCELLED'
  channel: PaymentChannel
  gateway: string
  amount: string
  currency?: string
  payLink: string | null
  qrPayload: string | null
  /** When the request was raised. Always present. */
  initiatedAt: string
  expiresAt: string | null
  paidAt?: string | null
  utr?: string | null
  failureReason?: string | null
}

export function useCasePayments(caseId: string | null | undefined) {
  return useQuery({
    queryKey: ['tele', 'payments', caseId],
    enabled: !!caseId,
    queryFn: async () => {
      const res = await api.get<Payment[]>(`/v1/cases/${caseId}/payments`)
      return res.data
    },
    // The customer is paying while the agent is still on the line, so this
    // has to notice the money arriving without the agent refreshing.
    refetchInterval: 5_000,
  })
}

export function useRaisePayment(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { amount: string; channel: PaymentChannel }) => {
      const res = await api.post<Payment>(`/v1/cases/${caseId}/payments`, body)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tele', 'payments', caseId] }),
  })
}

/**
 * Drive a mock payment to a terminal state.
 *
 * Mock mode only — the server 404s this for a LIVE tenant, checked against the
 * tenant's own setting rather than a build profile, so it cannot be left
 * switched on by a deployment mistake. The outcome is applied through the same
 * path a real webhook takes.
 */
export function useSimulatePayment(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { paymentId: string; success: boolean }) => {
      const res = await api.post<Payment>(`/v1/payments/${args.paymentId}/simulate`, {
        success: args.success,
        failureReason: args.success ? null : 'Simulated failure',
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tele', 'payments', caseId] }),
  })
}

// ---------------------------------------------------------------------------
// Scorecard
// ---------------------------------------------------------------------------

/**
 * Rates are `null`, not `0`, when the denominator is zero. An agent who made
 * no calls has no connect rate; rendering 0% would read as "called and never
 * got through", which is a different and worse thing to say about someone.
 */
export interface TeleScorecard {
  day: string
  attempts: number
  casesTouched: number
  connects: number
  rightPartyContacts: number
  promises: number
  promisedValue: string
  badNumbers: number
  callbacksScheduled: number
  callbacksDone: number
  callbacksMissed: number
  connectRate: string | null
  rpcRate: string | null
  promiseRate: string | null
  badNumberRate: string | null
  callbackAdherence: string | null
}

export function useTeleScorecard() {
  return useQuery({
    queryKey: ['tele', 'scorecard'],
    queryFn: async () => {
      const res = await api.get<TeleScorecard>('/v1/tele/scorecard')
      return res.data
    },
    refetchInterval: 60_000,
  })
}
